import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { EntityFilterProvider } from './context/EntityFilterContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import TicketList from './pages/TicketList';
import NewTicket from './pages/NewTicket';
import TicketDetail from './pages/TicketDetail';
import PendingAction from './pages/PendingAction';
import Users from './pages/Users';
import AdminPanel from './pages/AdminPanel';
import ThirdPartyForm from './pages/ThirdPartyForm';
import OffersPromoPage from './pages/OffersPromoPage';
import Flows from './pages/Flows';
import MagicLinkView from './pages/MagicLinkView';
import SampleCollections from './pages/SampleCollections';
import SampleCollectionDetail from './pages/SampleCollectionDetail';
import EmailMonitor from './pages/EmailMonitor';

/**
 * ProtectedRoute
 *
 * - No `page` prop  → any authenticated user passes.
 * - `page` prop     → checks the live permissions matrix from PermissionsContext.
 *
 * super_admin always passes regardless of the stored matrix.
 */
function ProtectedRoute({ children, page }) {
  const { user, loading }          = useAuth();
  const { canAccess, loadingPerms } = usePermissions();

  if (loading || loadingPerms) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (page && !canAccess(page, user)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />

      {/* Public third-party forms (no auth) */}
      <Route path="/qc-form/:ticketId"        element={<ThirdPartyForm formType="qc" />} />
      <Route path="/finance-form/:ticketId"   element={<ThirdPartyForm formType="finance" />} />
      <Route path="/dietitian-form/:ticketId" element={<ThirdPartyForm formType="dietitian" />} />
      <Route path="/logistics-form/:ticketId" element={<ThirdPartyForm formType="logistics" />} />

      {/* Public magic link read-only ticket view (no auth) */}
      <Route path="/view/:token" element={<MagicLinkView />} />

      {/* Protected routes — access governed by role_permissions table */}
      <Route path="/dashboard"   element={<ProtectedRoute page="dashboard"><Dashboard /></ProtectedRoute>} />
      <Route path="/tickets"     element={<ProtectedRoute page="tickets"><TicketList /></ProtectedRoute>} />
      <Route path="/pending"     element={<ProtectedRoute page="pending_action"><ErrorBoundary label="pending-action"><PendingAction /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/tickets/new"          element={<ProtectedRoute page="new_ticket"><NewTicket /></ProtectedRoute>} />
      <Route path="/tickets/hs/:hubspotId" element={<ProtectedRoute page="tickets"><ErrorBoundary label="ticket-detail"><TicketDetail /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/tickets/:id"           element={<ProtectedRoute page="tickets"><ErrorBoundary label="ticket-detail"><TicketDetail /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/users"       element={<ProtectedRoute page="users"><Users /></ProtectedRoute>} />
      <Route path="/admin"       element={<ProtectedRoute page="admin"><ErrorBoundary label="admin"><AdminPanel /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/offers"      element={<ProtectedRoute page="offers"><ErrorBoundary label="offers"><OffersPromoPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/flows"       element={<ProtectedRoute page="flows"><ErrorBoundary label="flows"><Flows /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/sample-collections"     element={<ProtectedRoute page="sample_collections"><ErrorBoundary label="sample-collections"><SampleCollections /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/sample-collections/:id" element={<ProtectedRoute page="sample_collections"><ErrorBoundary label="sample-collection-detail"><SampleCollectionDetail /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/email-monitor"          element={<ProtectedRoute page="email_monitor"><ErrorBoundary label="email-monitor"><EmailMonitor /></ErrorBoundary></ProtectedRoute>} />

      <Route path="/"  element={<Navigate to="/dashboard" replace />} />
      <Route path="*"  element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <PermissionsProvider>
              <EntityFilterProvider>
                <AppRoutes />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: { borderRadius: '10px', fontSize: '14px' },
                  }}
                />
              </EntityFilterProvider>
            </PermissionsProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
